import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Settings, X, BarChart3, Target, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useConsent } from '@/lib/consent';
import { cn } from '@/lib/utils';

interface FloatingConsentIconProps {
  className?: string;
}

export function FloatingConsentIcon({ className }: FloatingConsentIconProps) {
  const { consent, hasUserConsented, acceptAll, rejectAll, updateConsent } = useConsent();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Don't show if user hasn't consented yet (banner will handle this)
  if (!hasUserConsented) {
    return null;
  }

  const handleSavePreferences = () => {
    updateConsent({
      analytics: consent.analytics,
      marketing: consent.marketing,
      functional: true, // Always true
    });
    setIsModalOpen(false);
  };

  const getConsentStatus = () => {
    if (consent.analytics && consent.marketing) return 'all';
    if (!consent.analytics && !consent.marketing) return 'essential';
    return 'custom';
  };

  const consentStatus = getConsentStatus();

  return (
    <>
      {/* Floating Icon */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, duration: 0.3 }}
        className={cn(
          'fixed bottom-4 left-4 md:bottom-6 md:left-6 z-40',
          className
        )}
      >
        <motion.button
          onClick={() => setIsModalOpen(true)}
          onHoverStart={() => setIsHovered(true)}
          onHoverEnd={() => setIsHovered(false)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={cn(
            'relative w-10 h-10 md:w-12 md:h-12 rounded-full shadow-lg border-2',
            'flex items-center justify-center',
            'transition-all duration-200',
            'backdrop-blur-sm',
            consentStatus === 'all' && 'bg-green-500/20 border-green-500 text-green-400',
            consentStatus === 'essential' && 'bg-red-500/20 border-red-500 text-red-400',
            consentStatus === 'custom' && 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
          )}
          title="Manage Cookie Preferences"
        >
          <Shield className="h-4 w-4 md:h-5 md:w-5" />
          
          {/* Status indicator */}
          <div className={cn(
            'absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 w-2.5 h-2.5 md:w-3 md:h-3 rounded-full border-2 border-background',
            consentStatus === 'all' && 'bg-green-500',
            consentStatus === 'essential' && 'bg-red-500',
            consentStatus === 'custom' && 'bg-yellow-500'
          )} />
        </motion.button>

        {/* Tooltip on hover */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, x: -10, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -10, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="absolute left-14 top-1/2 -translate-y-1/2 pointer-events-none"
            >
              <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap border border-gray-700">
                Cookie Preferences
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Preferences Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[600px] bg-gray-950 border-gray-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Shield className="h-5 w-5 text-indigo-500" />
              Cookie Preferences
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Current Status */}
            <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg border border-gray-800">
              <div className="flex items-center gap-3">
                {consentStatus === 'all' && <CheckCircle className="h-5 w-5 text-green-500" />}
                {consentStatus === 'essential' && <XCircle className="h-5 w-5 text-red-500" />}
                {consentStatus === 'custom' && <Settings className="h-5 w-5 text-yellow-500" />}
                <div>
                  <p className="font-medium">
                    {consentStatus === 'all' && 'All Cookies Accepted'}
                    {consentStatus === 'essential' && 'Only Essential Cookies'}
                    {consentStatus === 'custom' && 'Custom Preferences'}
                  </p>
                  <p className="text-sm text-gray-400">
                    {consentStatus === 'all' && 'You\'ve accepted all cookie types'}
                    {consentStatus === 'essential' && 'Only necessary cookies are active'}
                    {consentStatus === 'custom' && 'You have custom cookie preferences'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsModalOpen(false)}
                className="border-gray-700 hover:bg-gray-800"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Cookie Categories */}
            <div className="grid grid-cols-1 gap-4">
              {/* Essential Cookies */}
              <Card className="bg-gray-900/50 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-green-500" />
                      Essential Cookies
                    </span>
                    <Switch checked={true} disabled />
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs text-gray-400 mb-2">
                    Required for basic site functionality, security, and remembering your preferences.
                  </p>
                  <div className="text-xs text-gray-500">
                    <strong>Always Active:</strong> These cannot be disabled as the website won't function properly without them.
                  </div>
                </CardContent>
              </Card>

              {/* Analytics Cookies */}
              <Card className="bg-gray-900/50 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-blue-500" />
                      Analytics Cookies
                    </span>
                    <Switch
                      checked={consent.analytics}
                      onCheckedChange={(checked) =>
                        updateConsent({ ...consent, analytics: checked })
                      }
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs text-gray-400 mb-2">
                    Help us understand how visitors interact with our website to improve user experience.
                  </p>
                  <div className="text-xs text-gray-500">
                    <strong>Used for:</strong> Google Analytics, performance monitoring, A/B testing
                  </div>
                </CardContent>
              </Card>

              {/* Marketing Cookies */}
              <Card className="bg-gray-900/50 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-pink-500" />
                      Marketing Cookies
                    </span>
                    <Switch
                      checked={consent.marketing}
                      onCheckedChange={(checked) =>
                        updateConsent({ ...consent, marketing: checked })
                      }
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs text-gray-400 mb-2">
                    Used to deliver relevant advertisements and measure campaign effectiveness.
                  </p>
                  <div className="text-xs text-gray-500">
                    <strong>Used for:</strong> Ad targeting, conversion tracking, social media integration
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-800">
              <div className="text-xs text-gray-500">
                Learn more in our{' '}
                <a href="/privacy-policy" className="text-indigo-400 hover:text-indigo-300 underline">
                  Privacy Policy
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={rejectAll}
                  className="border-gray-700 hover:bg-gray-800"
                >
                  Reject All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={acceptAll}
                  className="border-gray-700 hover:bg-gray-800"
                >
                  Accept All
                </Button>
                <Button
                  size="sm"
                  onClick={handleSavePreferences}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  Save Preferences
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}